/**
 * A date the trainer reads in their own language and picks with the browser's own calendar.
 *
 * ── Why the native input is not what is shown ────────────────────────────────────────────────
 * `<input type="date">` renders its value in the BROWSER's locale, not the app's: a Polish trainer
 * on an English-language Chrome read „08/31/2026" in the middle of a Polish form (owner, 4 November
 * 2026). The picker itself is fine — it is the platform's, accessible and keyboard-driven — so it
 * stays, and only the text changes: a button carries the date written by `Intl` in the app's
 * language („31.08.2026") and opens the native picker with `showPicker()`. The input stands under
 * the button, invisible and out of the tab order, so the picker anchors where the trainer looked.
 *
 * ── The name is the label AND the date ───────────────────────────────────────────────────────
 * A button named only by its `<label>` would hide its value from a listener, so the name is
 * composed: the label's id (or this component's own hidden label) followed by the date itself.
 */

import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { writtenCivilDate } from '~/shared/lib/calendarDate';

import hidden from './visuallyHidden.module.css';
import styles from './DateField.module.css';

export interface DateFieldProps {
  /** `YYYY-MM-DD`, or `null` while no date is chosen. */
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  /** The id of a visible label that names the field, or… */
  readonly labelId?: string;
  /** …the name itself, for a field with no visible label. */
  readonly label?: string;
  readonly min?: string;
  readonly max?: string;
  readonly invalid?: boolean;
  readonly describedBy?: string;
  readonly onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  /** The surface's own type and ink for the written date. */
  readonly className?: string;
}

export function DateField({
  value,
  onChange,
  labelId,
  label,
  min,
  max,
  invalid,
  describedBy,
  onBlur,
  className,
}: DateFieldProps) {
  const { t, i18n } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const ownLabelId = useId();
  const valueId = useId();

  const open = () => {
    const field = input.current;
    if (field === null) return;
    try {
      field.showPicker();
    } catch {
      // A browser without `showPicker` on a date input still opens its picker on a click.
      field.click();
    }
  };

  return (
    <span className={styles.root}>
      {label === undefined ? null : (
        <span id={ownLabelId} className={hidden.srOnly}>
          {label}
        </span>
      )}
      <button
        type="button"
        className={`${String(styles.button)} ${className ?? ''}`}
        aria-labelledby={`${labelId ?? ownLabelId} ${valueId}`}
        aria-invalid={invalid === true ? true : undefined}
        aria-describedby={describedBy}
        onBlur={onBlur}
        onClick={open}
      >
        <span id={valueId}>
          {value === null ? t('web:dateField.empty') : writtenCivilDate(value, i18n.language)}
        </span>
      </button>
      <input
        ref={input}
        className={styles.native}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value ?? ''}
        min={min}
        max={max}
        onChange={(event) => {
          onChange(event.target.value === '' ? null : event.target.value);
        }}
      />
    </span>
  );
}
